/**
 * FSP Representatives page — v31-1-10
 *
 * Route: /org/fsp-profile/representatives
 *
 * Features:
 * - List representatives from the FSP profile
 * - Add new representative
 * - Edit representative details
 * - Deactivate (never delete) representatives
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { FspProfile, FspRepresentative, RepresentativeCreateRequest } from '../../types/fspProfile';

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

const repApi = {
  getProfile: () => api.request<FspProfile>('/api/v1/orgs/fsp-profile'),
  createRep: (body: RepresentativeCreateRequest) =>
    api.request<FspRepresentative>('/api/v1/orgs/fsp-profile/representatives', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateRep: (id: number, body: Partial<RepresentativeCreateRequest>) =>
    api.request<FspRepresentative>(`/api/v1/orgs/fsp-profile/representatives/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deactivate: (id: number) =>
    api.request<FspRepresentative>(`/api/v1/orgs/fsp-profile/representatives/${id}/deactivate`, {
      method: 'PATCH',
    }),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FspRepresentatives: React.FC = () => {
  const qc = useQueryClient();
  const { data: profile, isLoading, error } = useQuery<FspProfile>({
    queryKey: ['fsp-profile'],
    queryFn: repApi.getProfile,
    retry: false,
  });

  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [repNumber, setRepNumber] = useState('');
  const [supervisedUntil, setSupervisedUntil] = useState('');

  const createMut = useMutation({
    mutationFn: repApi.createRep,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fsp-profile'] });
      resetForm();
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<RepresentativeCreateRequest> }) =>
      repApi.updateRep(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fsp-profile'] });
      resetForm();
    },
  });

  const deactivateMut = useMutation({
    mutationFn: repApi.deactivate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fsp-profile'] }),
  });

  const resetForm = () => {
    setShowAdd(false);
    setEditId(null);
    setName('');
    setRepNumber('');
    setSupervisedUntil('');
  };

  const startEdit = (rep: FspRepresentative) => {
    setEditId(rep.id);
    setName(rep.name);
    setRepNumber(rep.rep_number);
    setSupervisedUntil(rep.supervised_until ?? '');
    setShowAdd(false);
  };

  const handleSubmit = () => {
    const body: RepresentativeCreateRequest = {
      name,
      rep_number: repNumber,
      supervised_until: supervisedUntil || undefined,
    };

    if (editId) {
      updateMut.mutate({ id: editId, body });
    } else {
      createMut.mutate(body);
    }
  };

  const reps = profile?.representatives ?? [];
  const saving = createMut.isPending || updateMut.isPending;
  const mutError = createMut.error || updateMut.error;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">FSP Representatives</h1>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-700 dark:text-yellow-300 text-sm">
            Create an FSP profile first before adding representatives.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">FSP Representatives</h1>
        {!showAdd && !editId && (
          <button
            onClick={() => setShowAdd(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Add Representative
          </button>
        )}
      </div>

      {/* Add / Edit form */}
      {(showAdd || editId) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {editId ? 'Edit Representative' : 'New Representative'}
          </h2>
          {mutError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
              <p className="text-red-600 dark:text-red-400 text-sm">{(mutError as Error).message}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rep Number</label>
              <input
                type="text"
                value={repNumber}
                onChange={(e) => setRepNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Supervised Until
              </label>
              <input
                type="date"
                value={supervisedUntil}
                onChange={(e) => setSupervisedUntil(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={saving || !name || !repNumber}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {saving ? 'Saving...' : editId ? 'Update' : 'Add'}
            </button>
            <button
              onClick={resetForm}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Representatives table */}
      {reps.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
          No representatives added yet.
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Rep #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Supervised Until</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {reps.map((rep) => (
                <tr key={rep.id} className={!rep.active ? 'opacity-50' : ''}>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{rep.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{rep.rep_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {rep.supervised_until || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        rep.active
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {rep.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => startEdit(rep)}
                      className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                    >
                      Edit
                    </button>
                    {rep.active && (
                      <button
                        onClick={() => deactivateMut.mutate(rep.id)}
                        disabled={deactivateMut.isPending}
                        className="text-orange-600 dark:text-orange-400 hover:underline text-sm"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FspRepresentatives;
